// =======================================================
// CosmicSunburst — retro-futuristic data sun
// =======================================================
//
// Everything visible is a glyph: hex digits, binary, brackets, AI×DAY
// fragments. The "sun" is a dense swarm of tiny characters orbiting a
// hot core. The "rays" are streams of glyphs being ejected outward
// along radial lanes — they read as packets, not light. A faint hex
// haze fills the background, and HUD corners frame it as a console
// readout so the data origin is unmistakable.

import * as THREE from 'three';
import { Generator } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform sampler2D uMap;
  uniform vec3 uBg;
  void main() {
    vec4 t = texture2D(uMap, vUv);
    vec3 col = mix(uBg, t.rgb / max(t.a, 0.001), t.a);
    gl_FragColor = vec4(col, 1.0);
  }
`;

const HEX = '0123456789ABCDEF';
const BIN = '01';
const BRACKETS = '{}[]<>()/\\|';
const AI_FRAG = 'AIxDAY×∞◆';
const ALL_GLYPHS = (HEX + BIN + BIN + BRACKETS + AI_FRAG).split('');

function pickGlyph(rnd) {
  return ALL_GLYPHS[Math.floor(rnd * ALL_GLYPHS.length) % ALL_GLYPHS.length];
}

function hexToRgb(c) {
  if (typeof c === 'object' && c) {
    return { r: Math.round(c.r ?? 255), g: Math.round(c.g ?? 255), b: Math.round(c.b ?? 255) };
  }
  const h = String(c || '#ffffff').replace('#', '');
  if (h.length >= 6) return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
  return { r: 255, g: 255, b: 255 };
}
function rgba(c, a) {
  const { r, g, b } = hexToRgb(c);
  return `rgba(${r},${g},${b},${a})`;
}
function mixRgb(a, b, t) {
  const ca = hexToRgb(a), cb = hexToRgb(b);
  return {
    r: Math.round(ca.r + (cb.r - ca.r) * t),
    g: Math.round(ca.g + (cb.g - ca.g) * t),
    b: Math.round(ca.b + (cb.b - ca.b) * t),
  };
}
function rgbStr({ r, g, b }, a) { return `rgba(${r},${g},${b},${a})`; }

export class CosmicSunburst extends Generator {
  constructor() {
    super({
      name: 'Cosmic Sunburst',
      category: 'intense',
      schema: {
        coreCount:    { default: 1100, min: 100, max: 4000, step: 50,   label: 'Core glyphs' },
        rayLanes:     { default: 28,   min: 4,   max: 120,  step: 1,    label: 'Ray lanes' },
        rayDensity:   { default: 36,   min: 4,   max: 120,  step: 1,    label: 'Glyphs / lane' },
        rayLength:    { default: 0.95, min: 0.2, max: 1.5,  step: 0.01, label: 'Ray length' },
        emitSpeed:    { default: 0.55, min: 0,   max: 3,    step: 0.01, label: 'Emit speed' },
        spin:         { default: 0.06, min: -1,  max: 1,    step: 0.01, label: 'Sun spin' },
        coreRadius:   { default: 0.32, min: 0.05, max: 0.9, step: 0.01, label: 'Sun radius' },
        verticalSquash:{ default: 1.0, min: 0.4, max: 1.0,  step: 0.01, label: 'Sun roundness' },
        glyphPx:      { default: 14,   min: 6,   max: 36,   step: 1,    label: 'Glyph size (px)' },
        coreGlyphPx:  { default: 12,   min: 6,   max: 32,   step: 1,    label: 'Core glyph (px)' },
        bgHaze:       { default: 0.6,  min: 0,   max: 2,    step: 0.01, label: 'Background haze' },
        hudIntensity: { default: 0.7,  min: 0,   max: 1,    step: 0.01, label: 'HUD chrome' },
        scanlines:    { default: 0.18, min: 0,   max: 0.6,  step: 0.01, label: 'Scanlines' },
        vignette:     { default: 0.5,  min: 0,   max: 1,    step: 0.01, label: 'Vignette' },
        hot:          { default: '#fff4d6', type: 'color', label: 'Hot core' },
        warm:         { default: '#ffb347', type: 'color', label: 'Warm' },
        accent:       { default: '#ff4fd8', type: 'color', label: 'Magenta' },
        cool:         { default: '#39f5ff', type: 'color', label: 'Cyan' },
        background:   { default: '#070314', type: 'color', label: 'Background' },
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;
    this.off = document.createElement('canvas');
    this.offCtx = this.off.getContext('2d', { alpha: true });
    this._resizeCanvas(engine.width, engine.height);

    this.tex = new THREE.CanvasTexture(this.off);
    this.tex.minFilter = THREE.LinearFilter;
    this.tex.magFilter = THREE.LinearFilter;
    this.tex.generateMipmaps = false;

    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uMap: { value: this.tex },
        uBg:  { value: new THREE.Color(this.v('background')) },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
    this.track(this.tex);

    this._seed();
  }

  onResize(w, h) { this._resizeCanvas(w, h); }

  onParamChange(key) {
    if (!this.mesh) return;
    if (key === 'background') this.mesh.material.uniforms.uBg.value.set(this.v('background'));
    if (key === 'coreCount' || key === 'rayLanes' || key === 'rayDensity') this._seed();
  }

  reseed() {
    this.setSeed(((Math.random() * 0xffffffff) >>> 0) || 1);
    this._seed();
  }

  // Generator.rand01 returns roughly [-0.5, 0.5) (final XOR is signed).
  // Wrap negatives into [0, 1) so we get a uniform distribution instead
  // of values squashed into the lower half.
  _u01(i) {
    const r = this.rand01(i);
    return r < 0 ? r + 1 : r;
  }
  _rng(i, lo, hi) { return lo + (hi - lo) * this._u01(i); }

  _resizeCanvas(w, h) {
    const maxW = 4096;
    const maxH = 1080;
    this.off.width  = Math.max(2, Math.min(maxW, Math.round(w)));
    this.off.height = Math.max(2, Math.min(maxH, Math.round(h)));
    this._scale = this.off.width / Math.max(1, w);
  }

  _seed() {
    // Core swarm — particles orbiting at varying radii, packed tight near
    // the center and thinning outward to give the "sun" a soft edge.
    const coreN = Math.floor(this.v('coreCount'));
    this._core = new Array(coreN);
    for (let i = 0; i < coreN; i++) {
      // bias radius toward the center: u^1.6 pulls the distribution inward
      // so the sun packs dense at its core and thins toward its edge.
      const u = this._u01(11 + i * 3);
      const radius = Math.pow(u, 1.6);
      this._core[i] = {
        radius,                                     // 0..1 of sun radius
        angle:    this._rng(12 + i * 3, 0, Math.PI * 2),
        speedMul: this._rng(13 + i * 3, 0.6, 1.6),
        size:     this._rng(14 + i * 3, 0.7, 1.4),
        phase:    this._rng(15 + i * 3, 0, 1),
        glyph:    pickGlyph(this._u01(16 + i * 3)),
        glyphSwap:this._u01(17 + i * 3),
      };
    }

    // Background hex haze — sparse, dim, twinkling
    const hazeN = 320;
    this._haze = new Array(hazeN);
    for (let i = 0; i < hazeN; i++) {
      this._haze[i] = {
        x: this._u01(2000 + i * 2),
        y: this._u01(2001 + i * 2),
        glyph: HEX[Math.floor(this._u01(2002 + i * 2) * HEX.length) % HEX.length],
        phase: this._u01(2003 + i * 2),
        size:  this._rng(2004 + i * 2, 0.5, 1.0),
      };
    }

    // Lane glyph identity — each lane has a stable but slowly-rotating
    // sequence of glyphs so the stream feels coherent rather than noise.
    const lanes = Math.floor(this.v('rayLanes'));
    const perLane = Math.floor(this.v('rayDensity'));
    this._laneGlyphs = new Array(lanes);
    for (let l = 0; l < lanes; l++) {
      const arr = new Array(perLane);
      for (let k = 0; k < perLane; k++) {
        arr[k] = pickGlyph(this._u01(3000 + l * 137 + k * 7));
      }
      this._laneGlyphs[l] = arr;
    }
  }

  update(dt, time, engine) {
    const ctx = this.offCtx;
    const w = this.off.width;
    const h = this.off.height;
    const cx = w * 0.5;
    const cy = h * 0.5;

    ctx.clearRect(0, 0, w, h);

    // ---------- background gradient (subtle, not a flat fill) ----------
    const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.hypot(w, h) * 0.7);
    bg.addColorStop(0, rgba('#1a0d2e', 1));
    bg.addColorStop(0.5, rgba('#0a0518', 1));
    bg.addColorStop(1, rgba(this.v('background'), 1));
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    this._drawHaze(ctx, w, h, time);
    this._drawRays(ctx, w, h, cx, cy, time);
    this._drawCoreSwarm(ctx, w, h, cx, cy, time);
    this._drawHotCore(ctx, cx, cy, time);
    this._drawHud(ctx, w, h, time);
    this._drawScanlines(ctx, w, h);
    this._drawVignette(ctx, w, h);

    this.tex.needsUpdate = true;
  }

  // ---------- background haze of hex digits ----------
  _drawHaze(ctx, w, h, time) {
    const intensity = this.v('bgHaze');
    if (intensity <= 0) return;
    const px = Math.max(6, 9 * this._scale);
    ctx.save();
    ctx.font = `400 ${px}px 'Pumpa', 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    for (let i = 0; i < this._haze.length; i++) {
      const p = this._haze[i];
      const tw = 0.25 + 0.5 * (0.5 + 0.5 * Math.sin(time * 1.4 + p.phase * 6.28));
      ctx.fillStyle = rgba(this.v('cool'), tw * 0.18 * intensity);
      ctx.fillText(p.glyph, p.x * w, p.y * h);
    }
    ctx.restore();
  }

  // ---------- emission rays — streams of glyphs flying outward ----------
  _drawRays(ctx, w, h, cx, cy, time) {
    const lanes = this._laneGlyphs.length;
    const perLane = this._laneGlyphs[0]?.length ?? 0;
    const minDim = Math.min(w, h);
    const sunR = minDim * this.v('coreRadius');
    // Rays travel along an ellipse fit to the canvas, not a circle. On a
    // wide-and-thin wall this prevents diagonal particles from being culled
    // off-screen vertically before they ever reach the visible edges.
    const rayLen = this.v('rayLength');
    const radX = (w * 0.5 - sunR * 0.3) * rayLen;
    const radY = (h * 0.5 - sunR * 0.3) * rayLen;
    const spinPhase = time * this.v('spin');
    const emit = this.v('emitSpeed');
    const glyphPx = this.v('glyphPx') * this._scale;

    ctx.save();
    ctx.font = `400 ${glyphPx}px 'Pumpa', 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    for (let l = 0; l < lanes; l++) {
      const baseAngle = (l / lanes) * Math.PI * 2 + spinPhase + Math.sin(l * 12.9) * 0.01;
      // tiny per-lane wobble in time — feels like a live signal
      const wobble = Math.sin(time * 1.2 + l * 0.7) * 0.02;
      const angle = baseAngle + wobble;
      const ca = Math.cos(angle), sa = Math.sin(angle);

      // each glyph travels from the sun edge out to the canvas edge along
      // its own direction. Position is a phase (0..1) that increments with
      // time; we mod by 1 so glyphs loop.
      const lanePulse = 0.5 + 0.5 * Math.sin(time * 0.9 + l * 0.5);

      const sx = ca * sunR;
      const sy = sa * sunR;
      const ex = ca * radX;
      const ey = sa * radY;

      for (let k = 0; k < perLane; k++) {
        const phase0 = k / perLane;
        const phase = ((phase0 + time * (emit * 0.18 + 0.02 * (k % 3))) % 1 + 1) % 1;
        const eased = phase * phase * (3 - 2 * phase); // smoothstep

        const x = cx + sx + (ex - sx) * eased;
        const y = cy + sy + (ey - sy) * eased;
        if (x < -20 || x > w + 20 || y < -20 || y > h + 20) continue;

        // color: hot near core, magenta in mid, cyan at edges
        const t1 = Math.min(1, phase * 1.4);
        let color;
        if (t1 < 0.35) color = mixRgb(this.v('hot'), this.v('warm'), t1 / 0.35);
        else if (t1 < 0.7) color = mixRgb(this.v('warm'), this.v('accent'), (t1 - 0.35) / 0.35);
        else color = mixRgb(this.v('accent'), this.v('cool'), (t1 - 0.7) / 0.3);

        // alpha: fade in at start, fade out at end
        const headFade = Math.min(1, phase * 6);
        const tailFade = Math.min(1, (1 - phase) * 3);
        const a = headFade * tailFade * (0.55 + 0.45 * lanePulse);
        if (a < 0.02) continue;

        // pick glyph from the lane sequence + slow swap based on time
        const swap = Math.floor(time * 1.5) % perLane;
        const ch = this._laneGlyphs[l][(k + swap) % perLane];

        ctx.fillStyle = rgbStr(color, a);
        ctx.fillText(ch, x, y);
      }
    }
    ctx.restore();
  }

  // ---------- the sun: dense swarm of orbiting glyphs ----------
  _drawCoreSwarm(ctx, w, h, cx, cy, time) {
    const minDim = Math.min(w, h);
    const sunR = minDim * this.v('coreRadius');
    const squash = this.v('verticalSquash');
    const glyphPx = this.v('coreGlyphPx') * this._scale;

    ctx.save();
    ctx.font = `400 ${glyphPx}px 'Pumpa', 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    const spin = time * this.v('spin') * 1.6;
    for (let i = 0; i < this._core.length; i++) {
      const p = this._core[i];
      const a = p.angle + spin * p.speedMul;
      const r = sunR * p.radius;
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r * squash;

      // color gradient inside the sun: white-hot center → warm → magenta edge
      const t1 = p.radius;
      let color;
      if (t1 < 0.4) color = mixRgb(this.v('hot'), this.v('warm'), t1 / 0.4);
      else if (t1 < 0.85) color = mixRgb(this.v('warm'), this.v('accent'), (t1 - 0.4) / 0.45);
      else color = mixRgb(this.v('accent'), this.v('cool'), (t1 - 0.85) / 0.15);

      const flick = 0.7 + 0.3 * Math.sin(time * 6 + p.phase * 6.28);
      const edgeFade = Math.min(1, (1 - p.radius) * 1.4 + 0.25);
      const alpha = edgeFade * flick;

      // occasional glyph swap so the sun "computes"
      const swap = (Math.floor(time * 2 + p.glyphSwap * 10) % ALL_GLYPHS.length);
      const ch = ((p.glyphSwap * 7 + i) | 0) % 11 === 0
        ? ALL_GLYPHS[(ALL_GLYPHS.indexOf(p.glyph) + swap) % ALL_GLYPHS.length]
        : p.glyph;

      ctx.fillStyle = rgbStr(color, alpha);
      ctx.fillText(ch, x, y * 1); // y already squashed
    }
    ctx.restore();
  }

  // ---------- hot core highlight: small bright cluster, no solid disc ----------
  _drawHotCore(ctx, cx, cy, time) {
    // additive bright spot at center using composite mode
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const r = Math.min(this.off.width, this.off.height) * this.v('coreRadius') * 0.35;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    const pulse = 0.85 + 0.15 * Math.sin(time * 3.0);
    g.addColorStop(0, rgba(this.v('hot'), 0.85 * pulse));
    g.addColorStop(0.4, rgba(this.v('warm'), 0.35 * pulse));
    g.addColorStop(1, rgba(this.v('accent'), 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // ---------- HUD chrome (corner labels) — anchors the "data" reading ----------
  _drawHud(ctx, w, h, time) {
    const a = this.v('hudIntensity');
    if (a <= 0) return;
    const px = Math.max(10, 14 * this._scale);
    const pad = 18 * this._scale;

    ctx.save();
    ctx.font = `400 ${px}px 'Pumpa', 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textBaseline = 'top';

    const tick = Math.floor(time * 14);
    const sample = (n) => HEX[(tick * 7 + n * 13) & 0xf] + HEX[(tick * 11 + n * 5) & 0xf];
    const stamp = `${sample(1)}${sample(2)}-${sample(3)}${sample(4)}`;
    const thru = (180 + (Math.sin(time * 0.7) * 60)).toFixed(1);
    const samples = (1 + Math.floor(time * 31) % 9999).toString().padStart(4, '0');

    // top-left
    ctx.textAlign = 'left';
    ctx.fillStyle = rgba(this.v('cool'), 0.85 * a);
    ctx.fillText(`:: STREAM 0x${stamp} ::`, pad, pad);
    ctx.fillStyle = rgba(this.v('cool'), 0.45 * a);
    ctx.fillText(`THRU ${thru} kbps · ${samples} smp`, pad, pad + px * 1.2);

    // top-right
    ctx.textAlign = 'right';
    ctx.fillStyle = rgba(this.v('accent'), 0.85 * a);
    ctx.fillText(`AI×DAY 2026 · COSMIC SUNBURST`, w - pad, pad);
    ctx.fillStyle = rgba(this.v('accent'), 0.45 * a);
    ctx.fillText(`LANES ${this._laneGlyphs.length}  |  CORE ${this._core.length}`, w - pad, pad + px * 1.2);

    // bottom corners — frame ticks
    const tickLen = 22 * this._scale;
    ctx.strokeStyle = rgba(this.v('warm'), 0.55 * a);
    ctx.lineWidth = Math.max(1, 1.5 * this._scale);
    const corners = [
      [pad, pad],                  // tl
      [w - pad, pad],              // tr
      [pad, h - pad],              // bl
      [w - pad, h - pad],          // br
    ];
    for (const [x, y] of corners) {
      const dx = x < w / 2 ? 1 : -1;
      const dy = y < h / 2 ? 1 : -1;
      ctx.beginPath();
      ctx.moveTo(x + dx * 6, y + dy * 6);
      ctx.lineTo(x + dx * (6 + tickLen), y + dy * 6);
      ctx.moveTo(x + dx * 6, y + dy * 6);
      ctx.lineTo(x + dx * 6, y + dy * (6 + tickLen));
      ctx.stroke();
    }

    // bottom-left readout
    ctx.textAlign = 'left';
    ctx.fillStyle = rgba(this.v('cool'), 0.6 * a);
    ctx.fillText(`> emitting packets... ${(time * 1000 | 0) % 100000}`, pad + tickLen * 1.4, h - pad - px);

    // bottom-right uptime
    ctx.textAlign = 'right';
    ctx.fillStyle = rgba(this.v('warm'), 0.6 * a);
    const sec = Math.floor(time);
    const mm = String(Math.floor(sec / 60)).padStart(2, '0');
    const ss = String(sec % 60).padStart(2, '0');
    ctx.fillText(`UPTIME ${mm}:${ss} · LIVE`, w - pad - tickLen * 1.4, h - pad - px);

    ctx.restore();
  }

  _drawScanlines(ctx, w, h) {
    const amount = this.v('scanlines');
    if (amount <= 0) return;
    ctx.save();
    ctx.fillStyle = `rgba(0,0,0,${amount})`;
    const step = Math.max(2, Math.round(3 * this._scale));
    for (let y = 0; y < h; y += step) {
      ctx.fillRect(0, y, w, Math.max(1, this._scale));
    }
    ctx.restore();
  }

  _drawVignette(ctx, w, h) {
    const amount = this.v('vignette');
    if (amount <= 0) return;
    const cx = w * 0.5, cy = h * 0.5;
    const grad = ctx.createRadialGradient(cx, cy, Math.min(w, h) * 0.4, cx, cy, Math.hypot(w, h) * 0.55);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(1, `rgba(0,0,0,${amount})`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  }
}
